/**
 * Canonical level catalog and ShowConnect-division-name parsing.
 *
 * The catalog is hard-coded so the schedule UI, importers, and packet
 * output stay typed end-to-end. New levels are added by editing this
 * file (and the `LevelSlug` union).
 */
import { haversineMeters } from '../pairing/haversine.js';
const ORGANIZER_DESIGNATED_COLORS = {
    textColor: '#ffffff',
    backgroundColor: '#365a48',
};
const MODIFIED_COLORS = {
    textColor: '#f58220',
    backgroundColor: '#0057b8',
};
const PRELIMINARY_COLORS = {
    textColor: '#ffffff',
    backgroundColor: '#00843d',
};
const INTERMEDIATE_COLORS = {
    textColor: '#ffffff',
    backgroundColor: '#c8102e',
};
const ADVANCED_COLORS = {
    textColor: '#ffffff',
    backgroundColor: '#0057b8',
};
export const CANONICAL_LEVELS = [
    {
        slug: 'grasshopper',
        displayName: 'Introductory',
        difficultyRank: 10,
        colorScheme: { textColor: '#f58220', backgroundColor: '#00843d' },
        aliases: ['introductory', 'intro', 'grasshopper'],
    },
    {
        slug: 'starter',
        displayName: 'Starter',
        difficultyRank: 20,
        colorScheme: { textColor: '#f58220', backgroundColor: '#75d9f0' },
        aliases: ['starter'],
    },
    {
        slug: 'beginner-novice',
        displayName: 'Beginner Novice',
        difficultyRank: 30,
        colorScheme: { textColor: '#000000', backgroundColor: '#ffd800' },
        aliases: ['beginner novice', 'bn'],
    },
    {
        slug: 'novice',
        displayName: 'Novice',
        difficultyRank: 40,
        colorScheme: { textColor: '#000000', backgroundColor: '#ffffff' },
        aliases: ['novice'],
    },
    {
        slug: 'training',
        displayName: 'Training',
        difficultyRank: 50,
        colorScheme: { textColor: '#ffffff', backgroundColor: '#000000' },
        aliases: ['training'],
    },
    {
        slug: 'modified',
        displayName: 'Modified',
        difficultyRank: 60,
        colorScheme: MODIFIED_COLORS,
        aliases: ['modified'],
    },
    {
        slug: 'preliminary',
        displayName: 'Preliminary',
        difficultyRank: 70,
        colorScheme: PRELIMINARY_COLORS,
        aliases: ['preliminary', 'prelim'],
    },
    {
        slug: 'intermediate',
        displayName: 'Intermediate',
        difficultyRank: 80,
        colorScheme: INTERMEDIATE_COLORS,
        aliases: ['intermediate'],
    },
    {
        slug: 'advanced',
        displayName: 'Advanced',
        difficultyRank: 90,
        colorScheme: ADVANCED_COLORS,
        aliases: ['advanced'],
    },
    ...buildCciAliases(),
];
function buildCciAliases() {
    const result = [];
    for (let star = 1; star <= 5; star++) {
        for (const variant of ['short', 'long']) {
            const slug = `cci${star}s-${variant}`;
            const displayName = `CCI${star}*-${variant[0].toUpperCase()}${variant.slice(1)}`;
            const aliases = [
                `cci${star} ${variant}`,
                `cci${star}-${variant}`,
                `cci${star}${variant[0]}`,
                `cci ${star} ${variant}`,
            ];
            result.push({
                slug,
                displayName,
                difficultyRank: 100 + star * 10 + (variant === 'long' ? 1 : 0),
                colorScheme: cciColorScheme(star),
                aliases,
            });
        }
    }
    return result;
}
function cciColorScheme(star) {
    if (star === 1)
        return MODIFIED_COLORS;
    if (star === 2)
        return PRELIMINARY_COLORS;
    if (star === 3)
        return INTERMEDIATE_COLORS;
    if (star === 4)
        return ADVANCED_COLORS;
    return ORGANIZER_DESIGNATED_COLORS;
}
const SLUG_INDEX = new Map(CANONICAL_LEVELS.map((level) => [level.slug, level]));
export function getCanonicalLevel(slug) {
    const found = SLUG_INDEX.get(slug);
    if (!found) {
        throw new Error(`Unknown canonical level slug: ${slug}`);
    }
    return found;
}
export function tryCanonicalLevel(slug) {
    return SLUG_INDEX.get(slug) ?? null;
}
export function canonicalLevelDisplayName(slug) {
    return getCanonicalLevel(slug).displayName;
}
export function canonicalLevelColorScheme(slug) {
    return getCanonicalLevel(slug).colorScheme;
}
const CLEARLY_SEPARATE_DISTANCE_METERS = 30;
export function jumpCourseSuperfix(jump, visibleJumps) {
    const sequence = jumpSequence(jump.number);
    if (sequence == null)
        return null;
    const counterpart = visibleJumps.find((candidate) => equivalentCoursePair(jump.canonicalSlug, candidate.canonicalSlug) &&
        jumpSequence(candidate.number) === sequence);
    if (!counterpart || !clearlySeparateJump(jump, counterpart))
        return null;
    return isCciLevel(jump.canonicalSlug) ? 'C' : 'HT';
}
function isCciLevel(slug) {
    return slug.startsWith('cci');
}
function equivalentCoursePair(a, b) {
    return horseTrialsEquivalent(a) === b || horseTrialsEquivalent(b) === a;
}
function horseTrialsEquivalent(slug) {
    if (slug.startsWith('cci1s-'))
        return 'modified';
    if (slug.startsWith('cci2s-'))
        return 'preliminary';
    if (slug.startsWith('cci3s-'))
        return 'intermediate';
    if (slug.startsWith('cci4s-'))
        return 'advanced';
    return null;
}
function clearlySeparateJump(jump, counterpart) {
    if (normalizeJumpNumber(jump.number) !== normalizeJumpNumber(counterpart.number))
        return true;
    const name = normalizeJumpName(jump.name);
    const counterpartName = normalizeJumpName(counterpart.name);
    if (name && counterpartName && name !== counterpartName)
        return true;
    if (jump.latitude == null ||
        jump.longitude == null ||
        counterpart.latitude == null ||
        counterpart.longitude == null) {
        return false;
    }
    return (haversineMeters(jump.latitude, jump.longitude, counterpart.latitude, counterpart.longitude) > CLEARLY_SEPARATE_DISTANCE_METERS);
}
function jumpSequence(number) {
    const match = number.trim().match(/^\d+/);
    return match ? Number(match[0]) : null;
}
function normalizeJumpNumber(number) {
    return number.trim().toLowerCase().replace(/\s+/g, '');
}
function normalizeJumpName(name) {
    return (name ?? '')
        .normalize('NFKD')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
}
export function formatJumpTransitionLabel(slug, jumpNumber) {
    const prefixes = {
        grasshopper: 'intro',
        starter: 'st',
        'beginner-novice': 'bn',
        novice: 'n',
        training: 't',
        modified: 'm',
        preliminary: 'p',
        intermediate: 'i',
        advanced: 'a',
    };
    const prefix = prefixes[slug] ?? slug.replace(/s-short$/, 's').replace(/s-long$/, 'l');
    return `${prefix}-${jumpNumber}`;
}
/**
 * Normalize a ShowConnect division string so aliases match regardless
 * of punctuation / spacing / `*` style.
 */
export function normalizeDivisionName(name) {
    return name
        .toLowerCase()
        .replace(/\*/g, '*')
        .replace(/[^a-z0-9*]+/g, ' ')
        .replace(/\bcci\s*(\d+)\s*\*?\s*-?\s*(s|short)\b/g, 'cci$1 short')
        .replace(/\bcci\s*(\d+)\s*\*?\s*-?\s*(l|long)\b/g, 'cci$1 long')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Map a raw ShowConnect division string (e.g. "Open Training",
 * "Sr. Beginner Novice Rider A", "CCI4*-Short") to a canonical level
 * slug. Returns `null` when nothing matches.
 */
export function inferLevelSlug(divisionName) {
    const normalized = normalizeDivisionName(divisionName);
    if (!normalized)
        return null;
    const matches = CANONICAL_LEVELS.flatMap((level) => level.aliases.map((alias) => ({ slug: level.slug, alias, length: alias.length })))
        .sort((a, b) => b.length - a.length)
        .filter(({ alias }) => containsWordSequence(normalized, alias));
    return matches[0]?.slug ?? null;
}
function containsWordSequence(haystack, needle) {
    if (needle.length === 0)
        return false;
    return ` ${haystack} `.includes(` ${needle} `);
}
//# sourceMappingURL=levels.js.map